import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaType } from 'src/components/Chat/Chat.enum';
import { tAdReferral, tAttachment } from 'src/components/Chat/Chat.types';
import { Lightbox } from '../../Lightbox/Lightbox.component';

export type tAdReferralCardProps = {
  adReferral: tAdReferral;
};

/**
 * Returns `url` only when it is a string AND an https URL; otherwise null.
 *
 * Every creative URL inside `ad_referral` is attacker-influenceable Meta data,
 * so a single guard gates BOTH the inline <img src> and the URL handed to the
 * Lightbox — a non-https (or non-string, from backend shape drift) value opens
 * neither surface. A non-https URL would still be fetched by the browser
 * (tracking beacon / SSRF probe) if it reached <img src>. (R7.)
 */
function assertHttpsUrl(url: unknown): string | null {
  return typeof url === 'string' && url.startsWith('https://') ? url : null;
}

/**
 * Coerces an untrusted `ads_context_data` value to a string. Meta values are
 * typed loosely and can drift (new/changed sub-keys — INT-933); never assume a
 * string and never let an object reach a React child (React error #31). See
 * docs/solutions/logic-errors/unsafe-cast-masks-backend-shape-drift.md.
 */
function coerceString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * AdReferralCard — renders Meta's ad-referral context (Instagram CTIM /
 * Messenger CTM) as an inline chat-bubble card: the ad creative thumbnail, the
 * ad title as heading, and the `ad_id` for support/debug visibility. Clicking
 * the creative opens it in the in-app Lightbox (never a raw new-tab Meta CDN
 * URL). INT-972.
 *
 * Render floor: a payload with neither a usable (https) creative NOR a title —
 * the Messenger-CTM `ad_id`-only case — renders nothing. A creative-less,
 * title-less box would be visual noise on every ad-sourced message.
 *
 * The presentational shell mirrors SharePostContent (fixed `w-72`,
 * https-guarded thumbnail, `onError` placeholder, `loading="lazy"`,
 * `referrerPolicy="no-referrer"`, strings as text nodes), but the interaction
 * differs: SharePost wraps the whole card in an external `<a target="_blank">`;
 * this card owns a local Lightbox open-state and the thumbnail is decorative
 * (`aria-hidden`) inside a labeled "View ad" `<button>` that is the sole
 * keyboard/AT entry point to the viewer.
 *
 * CSP note: the creative URLs are RAW Meta CDN (`*.fbcdn.net` /
 * `*.cdninstagram.com` / `lookaside.fbsbx.com`), NOT Leadsales CDN. The deployed
 * gateway CSP `img-src` is `'self' data: https: http:` (nginx-api-gateway
 * prod/staging `configmap.yaml`), which permits any https origin — so the Meta
 * creatives DO load in production; no CSP amendment is needed. Because the CSP
 * even allows `http:`, `assertHttpsUrl` below is the actual guard preventing a
 * non-https creative from being fetched (the CSP would not block it). The
 * `onError` placeholder still covers the orthogonal case of an EXPIRED Meta CDN
 * URL (no S3 permanence — a deferred backend follow-up), where the card degrades
 * to title-only.
 *
 * Inbound-only by construction: MessageBubbleV2 (U5) mounts this only on inbound
 * bubbles, so it never renders on an outgoing/echo event. Kept strictly separate
 * from the WAPI/CTWA `content.referral` (different field + shape).
 */
export default function AdReferralCard(props: tAdReferralCardProps) {
  const { adReferral } = props;
  const { t } = useTranslation('chat/chat');
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  const contextData = adReferral.ads_context_data;
  const rawTitle = coerceString(contextData?.ad_title);
  // Prefer photo_url; fall back to video_url (a still image for video ads).
  // `||` (not `??`) so an empty-string photo_url falls through to video_url.
  const thumbUrl = assertHttpsUrl(
    contextData?.photo_url || contextData?.video_url,
  );
  const adId = coerceString(adReferral.ad_id);

  // Render floor: ad_id-only payload (no creative, no title) → render nothing.
  if (thumbUrl === null && rawTitle === '') {
    return null;
  }

  const heading = rawTitle || t('adLabel');
  const showThumbnail = thumbUrl !== null && !thumbnailFailed;

  // Synthesized single-image slide for the in-app viewer, built ONLY when a
  // usable (https) creative exists. The ad payload carries no MIME, and
  // parseSlides only yields an image slide when isImage(mimeType) is true — so
  // hardcode an image MIME. Null on the title-only path, so the viewer is never
  // rendered with a null-url slide even if a future refactor loosens the gate.
  const lightboxImage: tAttachment | null =
    thumbUrl === null
      ? null
      : {
          vcard: null,
          decryption: null,
          duration: null,
          filename: heading,
          latitude: null,
          longitude: null,
          mediaType: MediaType.IMAGE,
          mimeType: 'image/jpeg',
          url: thumbUrl,
        };

  return (
    <article
      className="w-72 mb-1 rounded-md overflow-hidden border border-base-200 bg-base-100 flex flex-col"
      data-testid="ad-referral-card"
    >
      {showThumbnail && (
        <button
          type="button"
          onClick={() => setIsViewerOpen(true)}
          aria-label={t('viewAd')}
          className="block w-full p-0 m-0 border-0 bg-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="ad-referral-view"
        >
          <img
            src={thumbUrl}
            alt=""
            aria-hidden
            className="w-full max-h-72 object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setThumbnailFailed(true)}
            data-testid="ad-referral-thumbnail"
          />
        </button>
      )}
      {thumbUrl !== null && thumbnailFailed && (
        <div
          className="w-full h-32 bg-base-200"
          aria-hidden
          data-testid="ad-referral-thumb-placeholder"
        />
      )}
      <div className="p-2 flex flex-col gap-1">
        <span
          className="line-clamp-2 text-sm font-medium"
          data-testid="ad-referral-title"
        >
          {heading}
        </span>
        {adId !== '' && (
          <span
            className="text-xs text-base-content/40 break-all"
            data-testid="ad-referral-adid"
          >
            {adId}
          </span>
        )}
      </div>
      {isViewerOpen && lightboxImage !== null && (
        <Lightbox
          isLightboxOpen={isViewerOpen}
          openAt={0}
          onClose={() => setIsViewerOpen(false)}
          attachments={[lightboxImage]}
        />
      )}
    </article>
  );
}
